/**
 * SEO utilities for generating meta tags from brand configuration
 */

export interface BrandConfig {
    brand: string;
    displayName: string;
    productName: string;
    productDescription: string;
    primaryColor: string;
    secondaryColor: string;
    logo: string;
    favicon: string;
    convertkit: {
        formId: string;
    };
    analytics: {
        plausibleDomain: string;
    };
    links: {
        privacy: string;
        terms: string;
        contact: string;
    };
}

/**
 * Generate meta tags from brand configuration
 * @param config - Brand configuration object
 * @returns Object with title and description
 */
export function toMeta(config: BrandConfig): { title: string; description: string } {
    return {
        title: `${config.displayName} - ${config.productName}`,
        description: config.productDescription
    };
}

/**
 * Generate Open Graph meta tags from brand configuration
 * @param config - Brand configuration object
 * @param url - Current page URL (optional)
 * @returns Object with Open Graph meta tags
 */
/**
 * Minimal Organization JSON-LD for rich results (brand home context).
 */
export function toOrganizationJsonLd(config: BrandConfig, url: string): Record<string, unknown> {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: config.displayName,
        url,
        logo: config.logo
    };
}

export function toOpenGraph(config: BrandConfig, url?: string): Record<string, string> {
    const meta = toMeta(config);

    return {
        'og:title': meta.title,
        'og:description': meta.description,
        'og:image': config.logo,
        'og:site_name': config.displayName,
        'og:type': 'website',
        ...(url && { 'og:url': url })
    };
}
